import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _dec, _dec2, _dec3, _class;
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { diContainer } from "@smartsheet/cufflink";
import { loggingStateServiceKey, LogLevel } from "@smartsheet/logging-state-service";
import { injectable } from "inversify";
import { combineSubjects, subjectify } from "./rxjsUtils";
var initialState = {
  bearerToken: null,
  bulkTaggedAssets: [],
  collectionKey: null,
  reviewOpened: false,
  userPermissions: null
};
// performance Metrics
export var FileLibraryPerformanceEvents = /*#__PURE__*/function (FileLibraryPerformanceEvents) {
  FileLibraryPerformanceEvents[FileLibraryPerformanceEvents["FILE_LIBRARY_PAGE_LOAD"] = 1] = "FILE_LIBRARY_PAGE_LOAD";
  FileLibraryPerformanceEvents[FileLibraryPerformanceEvents["FILE_VIEW_PAGE_LOAD"] = 2] = "FILE_VIEW_PAGE_LOAD";
  FileLibraryPerformanceEvents[FileLibraryPerformanceEvents["FILE_RASTER_UPLOAD"] = 3] = "FILE_RASTER_UPLOAD";
  FileLibraryPerformanceEvents[FileLibraryPerformanceEvents["FILE_VIDEO_UPLOAD"] = 4] = "FILE_VIDEO_UPLOAD";
  return FileLibraryPerformanceEvents;
}({});
export var ViewContext = /*#__PURE__*/function (ViewContext) {
  ViewContext["FILE_LIBRARY"] = "FILE_LIBRARY";
  ViewContext["FILE_VIEW"] = "FILE_VIEW";
  return ViewContext;
}({});
var defaultLogOptions = {
  awaitNextPaint: false,
  isSuccessful: true,
  skipLogEmit: false,
  timelineTrace: false
};
function isTabInFocus() {
  return document.visibilityState === "visible";
}
export var ContentLibraryStateService = (_dec = injectable(), _dec2 = Reflect.metadata("design:type", Function), _dec3 = Reflect.metadata("design:paramtypes", []), _dec(_class = _dec2(_class = _dec3(_class = class ContentLibraryStateService {
  /**
   * All subscribable subsets of data in the state service
   */

  constructor() {
    // observable built out of all the subjects
    // performance metrics
    _defineProperty(this, "viewContext", undefined);
    _defineProperty(this, "loggingIsPerfEventContextTuple", ["isPerfEvent", "true"]);
    /**
     * Set the reviewOpened state
     * @param reviewOpened New value for the reviewOpened state
     */
    _defineProperty(this, "setReviewOpened", reviewOpened => {
      this.subjects.reviewOpened$.next(reviewOpened);
    });
    _defineProperty(this, "setBearerToken", bearerToken => {
      this.subjects.bearerToken$.next(bearerToken);
    });
    _defineProperty(this, "setBulkTaggedAssets", assetIds => {
      this.subjects.bulkTaggedAssets$.next(assetIds);
    });
    _defineProperty(this, "setCollectionKey", collectionKey => {
      this.subjects.collectionKey$.next(collectionKey);
    });
    /**
     * Set the userPermissions state
     * @param userPermissions New va lue for the userPermissions state
     */
    _defineProperty(this, "setUserPermissions", userPermissions => {
      this.subjects.userPermissions$.next(userPermissions);
    });
    _defineProperty(this, "setContentLibraryDI", contentLibraryDI => {
      this.contentLibraryDI = contentLibraryDI;
    });
    /**
     * Take a snapshot of the state of the state service
     */
    _defineProperty(this, "getStateSnapshot", () => {
      return Object.keys(this.subjects).reduce((acc, key) => {
        var nonSubjectKey = key.slice(0, -1);
        return _objectSpread(_objectSpread({}, acc), {}, {
          [nonSubjectKey]: this.subjects[key].getValue()
        });
      }, {});
    });
    _defineProperty(this, "getContentLibraryDI", () => {
      return this.contentLibraryDI;
    });
    _defineProperty(this, "subscribeBulkTaggedAssets", callback => {
      return this.subjects.bulkTaggedAssets$.subscribe(callback);
    });
    /**
     * Subscribe to the entire state of the state service. Be careful using this as it can trigger unnecessary re-renders.
     * @param callback Function to call when anything in the state changes
     */
    _defineProperty(this, "subscribeFullState", callback => {
      return this.combinedStateObservable$.subscribe(callback);
    });
    /**
     * Subscribe to the reviewOpened state
     * @param callback Function to call when the reviewOpened state changes
     */
    _defineProperty(this, "subscribeReviewOpened", callback => {
      return this.subjects.reviewOpened$.subscribe(callback);
    });
    /**
     * Subscribe to the assetKey and bearerToken state
     * @param callback Function to call when the assetKey and bearerToken state changes
     */
    _defineProperty(this, "subscribeAppInitialization", callback => {
      return this.combinedStateObservable$.subscribe(_ref => {
        var bearerToken = _ref.bearerToken,
          collectionKey = _ref.collectionKey,
          userPermissions = _ref.userPermissions;
        return callback({
          bearerToken,
          collectionKey,
          userPermissions
        });
      });
    });
    /**
     * Subscribe to the userPermissions state
     * @param callback Function to call when the userPermissions state changes
     */
    _defineProperty(this, "subscribeUserPermissions", callback => {
      return this.subjects.userPermissions$.subscribe(callback);
    });
    this.subjects = subjectify(initialState);
    // combine all subjects into a single observable
    this.combinedStateObservable$ = combineSubjects(this.subjects);

    // performance metrics state
    this.cachedStartTimeMap = new Map();
    var isFdmEnabled = localStorage.getItem("reactAppLocalDev") === "true";
    var isPerfConsoleLoggingEnabled = localStorage.getItem("fl_perfConsoleLogging") === "true";
    if (isFdmEnabled && isPerfConsoleLoggingEnabled) {
      this.logger = {
        error: console.error,
        log: console.log,
        logClientEvent: console.log,
        logEventWithoutPrivateInfo: console.log,
        warn: console.log
      };
      this.logger.log(LogLevel.INFO, "PerformanceMetricsStateService: Using console logging due to reactAppLocalDev being set to true.");
    } else if (isFdmEnabled) {
      this.logger = {
        error: () => {},
        log: () => {},
        logClientEvent: () => {},
        logEventWithoutPrivateInfo: () => {},
        warn: () => {}
      };
      console.log("PerformanceMetricsStateService: Disabled perf logging due to reactAppLocalDev being set to true. Use fl_perfConsoleLogging = 'true' to debug perf logging while using FDM.");
    } else {
      this.logger = diContainer.get(loggingStateServiceKey.name);
    }
    this.globalEventContext = {};
  }
  /**
   * Performance Metrics methods
   * This code is copied from the Core Views Performance Metrics State Service:
   * https://git.lab.smartsheet.com/team-grid-views/core-views/-/blob/mainline/packages/performance-metrics-state-service/src/state-service/performanceMetricsStateService.ts
   */
  startEvent(event, context, options) {
    this.addEvent(event, context, options === null || options === void 0 ? void 0 : options.startTime);
  }
  stopAndLogEvent(event, context) {
    var logOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultLogOptions;
    if (logOptions !== defaultLogOptions) {
      var isInaccurate = logOptions.awaitNextPaint && !isTabInFocus();
      logOptions = _objectSpread(_objectSpread(_objectSpread({}, defaultLogOptions), logOptions), {}, {
        skipLogEmit: logOptions.skipLogEmit || isInaccurate
      });
    }
    this.logEventSynchronous(event, context, logOptions);
  }
  static generateContextKey(context) {
    return JSON.stringify(context);
  }
  popEvent(event, context) {
    // get the main event type
    var eventTraces = this.cachedStartTimeMap.get(event);
    if (eventTraces) {
      // see if this event has multiple outstanding times
      if (eventTraces.contexts) {
        var _contextTraces;
        // multiple traces running for same event type, see if we can find one based on context
        var key = ContentLibraryStateService.generateContextKey(context);
        var contextTraces = eventTraces.contexts.get(key);
        if (contextTraces) {
          contextTraces.startingTrace.isLast = contextTraces.curCount === 1;
          contextTraces.startingTrace.parallelCount = contextTraces.highestCount;
          contextTraces.curCount -= 1;
          // delete the container if it is no longer needed
          if (contextTraces.curCount < 1) {
            // no more traces for this context
            eventTraces.contexts.delete(key);
          }
          if (eventTraces.contexts.size == 0) {
            // no more trace for this event type
            this.cachedStartTimeMap.delete(event);
          }
        } else {
          // didn't find a matching context, get any(first) context so we can bump the call count down
          var _eventTraces$contexts = eventTraces.contexts.values();
          var _eventTraces$contexts2 = _slicedToArray(_eventTraces$contexts, 1);
          contextTraces = _eventTraces$contexts2[0];
          var _eventTraces$contexts3 = eventTraces.contexts.keys();
          var _eventTraces$contexts4 = _slicedToArray(_eventTraces$contexts3, 1);
          key = _eventTraces$contexts4[0];
        }
        return (_contextTraces = contextTraces) === null || _contextTraces === void 0 ? void 0 : _contextTraces.startingTrace;
      } else {
        // single event
        var trace = eventTraces.startingTrace;
        this.cachedStartTimeMap.delete(event);
        trace.isLast = true;
        trace.parallelCount = 0;
        return trace;
      }
    } else {
      var optionalIdMessage = context.id ? "for id ".concat(context.id, " ") : " ";
      var message = "Performance Event (".concat(FileLibraryPerformanceEvents[event], ") ").concat(optionalIdMessage, " has not been started yet, cannot stop and log");
      this.logger.warn(message, undefined,
      // exception
      new Map([this.loggingIsPerfEventContextTuple]), "content-library");
    }
  }
  logEventSynchronous(event, eventContext) {
    var logOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultLogOptions;
    var eventTrace = this.popEvent(event, eventContext);
    if (logOptions.skipLogEmit) {
      return;
    }
    if (eventTrace !== undefined) {
      // wait for the last event in case we are tracking multiple events in parallel
      if (!eventTrace.isLast) {
        return;
      }
      // see if this is a view specific event that we care about
      var clientEventName = FileLibraryPerformanceEvents[event];
      var clientEventId = event;
      var startTimeMs = eventTrace.performanceStart;
      // Prefix variable with "does" to satisfy linter requirement.
      var doesTimelineTrace = logOptions.timelineTrace,
        isSuccessful = logOptions.isSuccessful;
      var stopTimeMs = performance.now();
      // Round to nearest tenth of a millisecond.
      var elapsedTimeMs = Math.round((stopTimeMs - startTimeMs) * 10) / 10;
      var eventValue = elapsedTimeMs;
      var contextMap = new Map([["client_event_id", clientEventId.toString()], ["client_event_value", eventValue.toString()], ["client_event_success", isSuccessful !== undefined ? isSuccessful.toString() : "true"], this.loggingIsPerfEventContextTuple]);
      var countMsg = "";
      if (eventTrace.parallelCount && eventTrace.parallelCount > 1) {
        countMsg = " * ".concat(eventTrace.parallelCount);
      }

      // merge global and event specific contexts
      if (eventContext.id === undefined && this.globalEventContext.id !== undefined) {
        eventContext.id = this.globalEventContext.id;
      }
      var mergedContext = {
        metadata: _objectSpread(_objectSpread({}, this.globalEventContext), (eventContext === null || eventContext === void 0 ? void 0 : eventContext.metadata) || {})
      };
      var optionalIdMessage = eventContext.id ? "for id ".concat(eventContext.id) : "";
      var message = "Performance Event (".concat(clientEventName, ")").concat(countMsg, " ").concat(isSuccessful === false ? "failed" : "complete", " ").concat(optionalIdMessage, "\n") + "Metadata: ".concat(JSON.stringify(_objectSpread(_objectSpread({}, mergedContext === null || mergedContext === void 0 ? void 0 : mergedContext.metadata), {}, {
        elapsedTimeMs: elapsedTimeMs
      })));
      this.logger.log(
      // isSuccessful is optional so only treat as error if specifically false
      isSuccessful === false ? LogLevel.ERROR : LogLevel.INFO, message, undefined,
      // exception
      contextMap, "content-library", undefined,
      // appVersion
      eventTrace.viewContext ? ViewContext[eventTrace.viewContext] : undefined);
      if (doesTimelineTrace) {
        var measureDetails = {
          contextMetadata: eventContext.metadata || {},
          isSuccessful,
          loggingMetadata: logOptions.metadata || {}
        };
        if (eventTrace.parallelCount && eventTrace.parallelCount > 1) {
          measureDetails.parallelCount = eventTrace.parallelCount;
        }
        performance.measure(clientEventName, {
          detail: measureDetails,
          end: stopTimeMs,
          start: startTimeMs
        });
      }
    } else {
      var _optionalIdMessage = eventContext.id ? "for id ".concat(eventContext.id, " ") : " ";
      var _message = "Performance Event (".concat(FileLibraryPerformanceEvents[event], ") ").concat(_optionalIdMessage, " has not been started yet, cannot stop and log");
      this.logger.warn(_message, undefined,
      // exception
      new Map([this.loggingIsPerfEventContextTuple]), "content-library");
    }
  }
  addEvent(event, context, startTime) {
    var eventTypeTraces = this.cachedStartTimeMap.get(event);
    var performanceStart = startTime !== null && startTime !== void 0 ? startTime : performance.now();
    // see if this event is already pending.
    if (eventTypeTraces) {
      // Not the first event of this type.
      // See if this is the second event of this type (no context map created yet)
      if (!eventTypeTraces.contexts) {
        // second instance of the event.  Create a map to store all instances based on context
        eventTypeTraces.contexts = new Map();
        // and copy the starting trace into the map
        eventTypeTraces.contexts.set(ContentLibraryStateService.generateContextKey(eventTypeTraces.startingTrace.context), {
          curCount: 1,
          highestCount: 1,
          startingTrace: _objectSpread({}, eventTypeTraces.startingTrace)
        });
      }
      // see if we have seen this context before
      var key = ContentLibraryStateService.generateContextKey(context);
      var contextTraces = eventTypeTraces.contexts.get(key);
      if (!contextTraces) {
        // have not seen this context before
        eventTypeTraces.contexts.set(key, {
          curCount: 1,
          highestCount: 1,
          startingTrace: {
            context,
            performanceStart,
            viewContext: this.viewContext
          }
        });
      } else {
        contextTraces.highestCount += 1;
        contextTraces.curCount += 1;
      }
    } else {
      // no outstanding instances
      eventTypeTraces = {
        startingTrace: {
          context,
          performanceStart,
          viewContext: this.viewContext
        }
      };
    }
    this.cachedStartTimeMap.set(event, eventTypeTraces);
  }
}) || _class) || _class) || _class);
//# sourceMappingURL=contentLibraryStateService.js.map